import React, { useEffect, useState } from 'react'
import moment from 'moment';
import { Button } from '@mui/material';
import { AppConfig } from '../../config/appConfig'

import { IMaterialPriceSearchRequest, IMaterialPriceSearchResult } from '../../types/material';

import { Icons } from '../icons';
import { NavLink } from 'react-router-dom';
import Spinner from '../spinner';
import { useListContext } from '../../contexts/listContext';

export interface IMaterialPriceTableProps {
	data?: IMaterialPriceSearchResult;
	material?: string;
	customer?: string;
	vendor?: string;
	status?: string;
	isAwaitingResultSet?: boolean;
	update?: Function;
}

const MaterialPriceTable: React.FunctionComponent<IMaterialPriceTableProps> = ({ data, material, customer, vendor, status, isAwaitingResultSet, update }) => {
	const listContext = useListContext();
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.materialPriceListDefaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.materialPriceListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);
	const [searchRequest, setSearchRequest] = useState({
		pageIndex: 0,
		pageSize: pageSize,
		customer: customer,
		material: material,
		vendor: vendor,
		status: status,
		sortField: sortField,
		sortAscending: sortAscending
	});

	useEffect(() => {
		const request: IMaterialPriceSearchRequest = {
			pageIndex: 0,
			pageSize: searchRequest.pageSize,
			customer: customer,
			material: material,
			vendor: vendor,
			status: status,
			sortField: sortField,
			sortAscending: sortAscending
		};
		setSearchRequest(request);
	}, [material, customer, vendor, status])

	useEffect(() => {
		setPageIndex(searchRequest.pageIndex);
		setPageSize(searchRequest.pageSize);
		setSortField(searchRequest.sortField);
		setSortAscending(searchRequest.sortAscending);

		update(searchRequest);
	}, [searchRequest]);

	const handleHeaderClick = (field: string) => {
		if (isAwaitingResultSet) return;
		const request = {...searchRequest};

		if (sortField == field) {
			request.sortAscending = !sortAscending;
		}
		else {
			request.sortAscending = true;
			request.sortField = field;
		}
		request.pageIndex = 0;

		setSearchRequest(request);
	}

	const handlePageBack = () => {
		if (pageIndex > 0) {
			const request = {...searchRequest, ['pageIndex']: pageIndex - 1}
			setSearchRequest(request);
		}
	}

	const handlePageForward = () => {
		if (pageIndex < (Math.floor(data?.totalMatchingRecords / pageSize))) {
			const request = {...searchRequest, ['pageIndex']: pageIndex + 1};
			setSearchRequest(request);
		}
	}

	const getContainerName = (id: number | undefined) => {
		let container = 'unknown';
		if(id){
			let containers = listContext.containers.filter(c => c.id == id);
			if (containers.length > 0){
				container = containers[0].name;
			}
		}
		
		return container;
	}

	return (
		<div className="hwi-entity-search-results-table ">
			<div className="headers">
			<div className="vendor-col">
					<p onClick={() => handleHeaderClick("vendor")}>
						Vendor
						{" "}
						{(sortField == "vendor" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "vendor" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="customer-col">
					<p onClick={() => handleHeaderClick("customer")}>
						Customer
						{" "}
						{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="name-col">
					<p onClick={() => handleHeaderClick("material")}>
						Material
						{" "}
						{(sortField == "material" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "material" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="category-col">
					<p onClick={() => handleHeaderClick("category")}>
						Code
						{" "}
						{(sortField == "category" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "category" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="price-col">
					<p onClick={() => handleHeaderClick("price")}>
						Price
						{" "}
						{(sortField == "price" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "price" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="uom-col">
					<p onClick={() => handleHeaderClick("uom")}>
						UOM
						{" "}
						{(sortField == "uom" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "uom" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="start-col">
					<p onClick={() => handleHeaderClick("start")}>
						Effective
						{" "}
						{(sortField == "start" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "start" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="end-col">
					<p onClick={() => handleHeaderClick("end")}>
						End
						{" "}
						{(sortField == "end" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "end" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="controls-col">
				</div>
			</div>
			<div className="data">
			<Spinner dependency={isAwaitingResultSet} text="Loading Materials...">
				{data?.records.length > 0 ?
					(
						data?.records.map((item) => (
							<div className={"row"} key={item.id}>
								<div className="vendor-col"><p>{item.vendorName}</p></div>
								<div className="customer-col"><p>{item.customerName}</p></div>
								<div className="name-col"><p>{item.materialName}</p></div>
								<div className="category-col"><p>{item.customerCategoryName || "-"}</p></div>
								<div className="price-col"><p>{(item.priceByContainer ?? false) ? item.containerPrice : item.price}</p></div>
								<div className="uom-col"><p>{((item.priceByContainer ?? false) ? getContainerName(item.containerId) : item.uomName).replace("transportation", "trans-portation")}</p></div>
								<div className="start-col"><p>{moment(item.startDate).format("M/D/YY")}</p></div>
								<div className="end-col"><p>{item.endDate ? moment(item.endDate).format("M/D/YY") : "-"}</p></div>
								<div className="controls-col">
									<p>
										{!item?.endDate ?
											<NavLink to={"/materialprice/" + item.id}>
												<Icons.EditItem fill="#272f50" height="14px"></Icons.EditItem>
												{" "}
												Edit
											</NavLink> : ""}
									</p>
								</div>
							</div>
						))
					) :
					(
						<div className="no-results">0 results</div>
					)
				}
				</Spinner>
			</div>
			<div className="pager">
				{!isAwaitingResultSet && data?.records.length > 0 &&
					<>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={isAwaitingResultSet || pageIndex <= 0} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${(pageIndex * pageSize) + 1} to ${Math.min((pageIndex * pageSize) + pageSize, data?.totalMatchingRecords)} of ${data?.totalMatchingRecords}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={isAwaitingResultSet || ((pageIndex + 1) * pageSize) > data?.totalMatchingRecords} onClick={handlePageForward}>Next</Button></div>
					</>
				}
			</div>
			{/* <Json data={data}></Json> */}
		</div>
	)
}

export default MaterialPriceTable;